/**
 * The last few prompts, for the player who asked to see them.
 *
 * ## Why this is not in `SessionState`
 *
 * It is diagnostics, not music. The reducer's state is the thing that gets persisted,
 * restored, replayed and compared in tests; a ring of prompt text riding along in it would
 * be written to IndexedDB on every autosave, restored into a session that has nothing to
 * do with it, and would make every deep-compare in `chordInput.test.ts` a comparison of
 * prompt strings. So it is a plain module with a subscription, in the same spirit as
 * `commands/voice.ts` owning the spoken-replies preference: state that belongs to the
 * device and the moment rather than to the progression.
 *
 * ## Costing nothing while it is off
 *
 * Nothing here is called unless "Show model prompts" is on, because nothing sends
 * `debug: true` unless it is on. The ring is a module-level array that starts and stays
 * empty, the sheet is not mounted, and the requests are byte-for-byte the ones that were
 * sent before this feature existed.
 */

import type { LlmDebugPayload } from '../api/index.js';

/**
 * How many entries are kept. Twenty is about five chords' worth of look-ahead and answers
 * the question the sheet exists for ("did this one come from the cache?") without turning
 * a diagnostics panel into a session log.
 */
export const DEBUG_LOG_LIMIT = 20;

/**
 * Which request this was.
 *
 * `branches:look-ahead` is a speculative request the prefetcher made about a chord nobody
 * has played yet, and it is called out separately on purpose: whether look-ahead is
 * working is the exact question this whole sheet was built to answer, and a speculative
 * request that is followed by no real one is what "it worked" looks like.
 */
export type DebugRoute = 'branches' | 'branches:look-ahead' | 'command' | 'explain';

export interface DebugEntry extends LlmDebugPayload {
  /** Monotonic within the page. The sheet's React key, and the only ordering that matters. */
  id: number;
  route: DebugRoute;
  /** Wall clock, for the "3s ago" line. */
  at: number;
}

type Listener = (entries: readonly DebugEntry[]) => void;

/** Newest first — the sheet is read from the top and the last request is the interesting one. */
let entries: readonly DebugEntry[] = [];
const listeners = new Set<Listener>();
let nextId = 1;

/** The ring as it stands. Stable between changes, so it is safe as a `getSnapshot`. */
export function getDebugEntries(): readonly DebugEntry[] {
  return entries;
}

export function subscribeToDebugLog(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Record one answered request. Called only while the setting is on. */
export function recordDebugEntry(
  route: DebugRoute,
  debug: LlmDebugPayload,
  now: () => number = Date.now,
): DebugEntry {
  const entry: DebugEntry = { ...debug, id: nextId, route, at: now() };
  nextId += 1;
  entries = [entry, ...entries].slice(0, DEBUG_LOG_LIMIT);
  for (const listener of [...listeners]) listener(entries);
  return entry;
}

export function clearDebugLog(): void {
  if (entries.length === 0) return;
  entries = [];
  for (const listener of [...listeners]) listener(entries);
}

/** Tests only: forget the ring *and* its subscribers, so one test cannot leak into the next. */
export function resetDebugLog(): void {
  entries = [];
  listeners.clear();
  nextId = 1;
}
